import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Check, MapPin } from "lucide-react";
import api from "@/lib/api";
import { FadeUp, MaskedLines } from "@/components/Reveal";
import ProductCard from "@/components/ProductCard";
import EnquiryForm from "@/components/EnquiryForm";
import SEO from "@/components/SEO";

const ProductDetail = () => {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    api
      .get(`/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-6 text-center" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-bold">Hybrid not found</h1>
        <Link to="/products" className="mt-6 text-sm font-bold text-brand-400" data-testid="back-to-products-link">
          ← Back to Products
        </Link>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex min-h-screen items-center justify-center" data-testid="product-loading">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-sage">Loading hybrid profile…</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title={`${product.name} Hybrid Seeds`}
        description={`${product.name} — ${product.crop.replace(/-/g, " ")} by Seed Park India. ${product.tagline}`}
      />
      <section data-testid="product-hero" className="relative overflow-hidden pt-36 pb-20">
        <div className="absolute inset-0">
          <img src={product.backdrop || product.image} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-white/80 via-white/85 to-white" />
        </div>
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Link to={`/products?crop=${product.crop}`} data-testid="product-back-link" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-sage transition-colors hover:text-brand-400">
              <ArrowLeft className="h-4 w-4" /> {product.crop.replace("-", " ")} portfolio
            </Link>
          </FadeUp>
          <div className="mt-10 grid items-end gap-12 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <MaskedLines lines={[product.name]} delay={0.1} className="font-display text-5xl font-black tracking-tight sm:text-6xl lg:text-7xl" />
              <FadeUp delay={0.35}>
                <p className="mt-6 max-w-xl text-base leading-relaxed text-mist md:text-lg">{product.tagline}</p>
              </FadeUp>
            </div>
            <div className="lg:col-span-5">
              <FadeUp delay={0.2}>
                <div className="relative overflow-hidden border border-[#123C6B]/10 bg-white shadow-[0_24px_60px_rgba(18,60,107,0.10)]">
                  <img src={product.image} alt={product.name} className={product.packshot ? "aspect-[4/3] w-full object-contain p-8" : "aspect-[4/3] w-full object-cover"} />
                  {!product.packshot && <div className="absolute inset-0 bg-gradient-to-t from-white/60 to-transparent" />}
                  <p className="absolute bottom-4 left-4 bg-white/80 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-corp-600 backdrop-blur-sm">{product.packshot ? "Certified seed pack" : "Field performance — multi-location trials"}</p>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="product-details" className="py-20 lg:py-28">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-12 lg:px-10">
          <div className="lg:col-span-7">
            <FadeUp>
              <h2 className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-brand-400">
                <span className="inline-block h-px w-8 bg-brand-400" /> Key Features
              </h2>
            </FadeUp>
            <ul className="mt-8 space-y-5" data-testid="product-features">
              {product.features.map((f, i) => (
                <FadeUp key={i} delay={i * 0.05}>
                  <li className="flex gap-4 border-b border-white/10 pb-5">
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand-400" />
                    <p className="text-base leading-relaxed text-foreground">{f}</p>
                  </li>
                </FadeUp>
              ))}
            </ul>

            <FadeUp delay={0.1}>
              <h2 className="mt-16 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.3em] text-corp-500">
                <span className="inline-block h-px w-8 bg-corp-500" /> Recommended Cultivation Areas
              </h2>
            </FadeUp>
            <FadeUp delay={0.15}>
              <p className="mt-6 text-sm leading-relaxed text-mist">
                Cultivation suitable across all the following regions of India.
              </p>
            </FadeUp>
            <div className="mt-6 grid gap-5 sm:grid-cols-3" data-testid="product-regions">
              {[
                { zone: "Southern India", states: "Telangana • Andhra Pradesh • Karnataka • Tamil Nadu • Kerala" },
                { zone: "Northern India", states: "Punjab • Haryana • Uttar Pradesh • Rajasthan • Delhi NCR" },
                { zone: "Central India", states: "Madhya Pradesh • Maharashtra • Chhattisgarh • Gujarat" },
              ].map((z) => (
                <div key={z.zone} className="border border-[#123C6B]/10 bg-forest-900 p-6">
                  <p className="flex items-center gap-2 font-display text-lg font-bold tracking-tight text-foreground">
                    <MapPin className="h-4 w-4 text-brand-400" /> {z.zone}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-sage">{z.states}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <FadeUp delay={0.1}>
              <div className="lg:sticky lg:top-28">
                <EnquiryForm type="product" productSlug={product.slug} heading={`Enquire about ${product.name}`} testId="product-enquiry" />
                <p className="mt-4 text-xs leading-relaxed text-sage">
                  Our agronomy team responds within one working day with availability, pack sizes and
                  region-specific advisory.
                </p>
              </div>
            </FadeUp>
          </div>
        </div>
      </section>

      {product.related?.length > 0 && (
        <section data-testid="product-related" className="border-t border-white/10 bg-forest-900 py-20 lg:py-28">
          <div className="mx-auto max-w-7xl px-6 lg:px-10">
            <FadeUp>
              <h2 className="mb-10 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                More from the {product.crop.replace("-", " ")} portfolio
              </h2>
            </FadeUp>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {product.related.map((p, i) => (
                <ProductCard key={p.slug} product={p} index={i} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
};

export default ProductDetail;
